import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Translate } from 'react-localize-redux';
import { InsuranceBill, InsuranceBillClaim, DocumentStatus, CreatePaymentRequest, PaymentRecord, BillItem, DocumentType, Bill, PaymentMethod } from './types';
import { Box, Button, Card, Chip, Grid, Typography, Snackbar, Alert, Dialog, DialogTitle, DialogContent, DialogActions, IconButton } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { formatCurrency, fullDateFromPostgresString, stringDatePostgresToDate, researcherFullName } from '../../../utils';
import { documentStatusToIcon } from '.';
import { documentStatusToColor } from '../../../utils/bill';
import { ColourChip } from '../../../components/general/mini_components-ts';
import { useInsuranceBill } from '../../../hooks/useInsuranceBill';
import { closeInsuranceBillService, updateBillItemsStatusService, getBillByUuidService } from '../../../services/billing';
import { useSnackBarState, useDepartments } from '../../../hooks';
import Loader from '../../../components/Loader';
import { IconGenerator } from '../../../components/general/mini_components';
import Modal from '../../../components/general/modal';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import CloseIcon from '@mui/icons-material/Close';
import { usePDFConfig } from '../../../hooks/usePDFConfig';
import { BillItems } from './BillItems';
import { BillItemModes } from './types';
import { useDispatch, useSelector } from 'react-redux';
import { saveBillingItems } from '../../../redux/actions/billingActions';
import { INSURANCE_BILL_COLUMNS } from './insuranceBillColumns';
import { generateInsuranceBillPDF, getBillIdentifier, enhanceBillItemsWithPatientData, formatPatientName, getPatientUuid, fetchPatientsForBillItems } from './insuranceBillUtils';
import PaymentRecordsDisplay from './PaymentRecordsDisplay';
import PaymentRecordsModal from './PaymentRecordsModal';

import { IPatient } from '../../../constants/types';

// Constants
const ERROR_CODE_NO_BILL_ITEMS = 'NO_BILL_ITEMS';
const SNACKBAR_AUTO_HIDE_DURATION = 4000;

interface InsuranceBillViewProps {
    investigationUuid: string;
    billUuid: string;
    currency: string;
    languageCode: string;
    onClose?: () => void;
    onBillUpdated?: () => void;
    // For Storybook/testing - bypass hook if provided
    bill?: InsuranceBill | null;
    claims?: InsuranceBillClaim[];
    loading?: boolean;
    error?: string | null;
    // Required: Fetch function - real implementation in app, mock in Storybook
    fetchPatientFn: (uuidPatient: string) => Promise<IPatient | null>;
}

const InsuranceBillView: React.FC<InsuranceBillViewProps> = ({
    investigationUuid,
    billUuid,
    currency,
    languageCode,
    onClose,
    onBillUpdated,
    bill: providedBill,
    claims: providedClaims,
    loading: providedLoading,
    error: providedError,
    fetchPatientFn
}) => {
    // Use provided data for Storybook/testing, otherwise use hook
    const hookResult = useInsuranceBill({
        investigationUuid,
        billUuid,
        enabled: !providedBill // Disable hook if bill is provided
    });

    const bill = providedBill !== undefined ? providedBill : hookResult.bill;
    const claims = providedClaims || hookResult.claims;
    const loading = providedLoading !== undefined ? providedLoading : hookResult.loading;
    const error = providedError !== undefined ? providedError : hookResult.error;
    const refetch = hookResult.refetch;

    const [showCloseConfirm, setShowCloseConfirm] = useState(false);
    const [showModalPDF, setShowModalPDF] = useState(false);
    const [showModalPaymentRecords, setShowModalPaymentRecords] = useState(false);
    const [closing, setClosing] = useState(false);
    const [showSnackbar, setShowSnackbar, handleCloseSnackbar] = useSnackBarState();
    const [paymentRecords, setPaymentRecords] = useState<PaymentRecord[]>([]);
    const [patientsMap, setPatientsMap] = useState<Record<string, IPatient>>({});
    const { config: pdfConfig } = usePDFConfig();
    const [generatingPDF, setGeneratingPDF] = useState(false);
    const dispatch = useDispatch();
    const { researchers } = useDepartments();
    const paymentMethods = useSelector((state: any) => state.investigations.currentInvestigation.billingInfo.paymentMethods);

    const researcherMap = useMemo(() => {
        const map: Record<string, any> = {};
        researchers.forEach((r) => { map[r.uuid] = r; });
        return map;
    }, [researchers]);

    // Fetch patients for billItems - get patientUuids directly from billItems
    useEffect(() => {
        const fetchPatients = async () => {
            const patients = await fetchPatientsForBillItems(bill?.billItems || [], fetchPatientFn);
            setPatientsMap(patients);
        };

        fetchPatients();
    }, [bill?.billItems, fetchPatientFn]);

    // Enhance billItems from server with client-side calculated/derived fields
    // The server provides complete billItems with all core data (concept, amount, paid, insuranceClaim, patientUUID, etc.)
    // We only need to add: patientName (from patientsMap) and patientPaymentAmount (calculated)
    const billItemsWithPatientData = useMemo(() => {
        // Server should always provide billItems - if not, return empty array
        if (!bill?.billItems || bill.billItems.length === 0) {
            return [];
        }

        return enhanceBillItemsWithPatientData(bill.billItems, patientsMap);
    }, [bill?.billItems, patientsMap]);

    const refetchPaymentRecords = useCallback(async () => {
        if (!bill?.uuid || !investigationUuid) return;
        
        try {
            const response = await getBillByUuidService(investigationUuid, bill.uuid);
            // Response may have paymentRecords on the bill object (not in Bill type, but returned by API)
            const billWithPayments = response.bill as unknown as InsuranceBill & { paymentRecords?: PaymentRecord[] };
            setPaymentRecords(billWithPayments?.paymentRecords || []);
        } catch (e) {
            console.error('[InsuranceBillView] Error fetching payment records:', e);
            setPaymentRecords([]);
        }
    }, [bill?.uuid, investigationUuid]);

    // Fetch payment records when bill changes
    useEffect(() => {
        if (bill?.uuid && investigationUuid) {
            refetchPaymentRecords();
        }
    }, [bill?.uuid, investigationUuid, refetchPaymentRecords]);

    // Load billItems with patient data into Redux when they change
    useEffect(() => {
        if (billItemsWithPatientData.length > 0) {
            dispatch(saveBillingItems(billItemsWithPatientData));
        }
    }, [billItemsWithPatientData, dispatch]);

    const remainingBalance = useMemo(() => {
        return bill ? bill.totalGross - bill.totalPaid : 0;
    }, [bill]);

    const claimsCount = claims?.length || 0;

    const totalPaid = useMemo(() => {
        return paymentRecords.reduce((sum, rec) => sum + rec.amount, 0);
    }, [paymentRecords]);

    const totalPending = useMemo(() => {
        // totalPending is the same as remainingBalance since bill.totalPaid already includes all payments
        return remainingBalance;
    }, [remainingBalance]);

    const handleCloseBill = async () => {
        if (!bill) return;

        setClosing(true);
        try {
            await closeInsuranceBillService(investigationUuid, bill.uuid);
            setShowSnackbar({
                show: true,
                message: 'pages.hospital.billing.insurance_bills.success.bill_closed',
                severity: 'success'
            });
            setShowCloseConfirm(false);
            refetch();
            onBillUpdated?.();
        } catch (err: unknown) {
            // Check for specific error codes
            const errorCode = (err as any)?.response?.data?.errorCode || (err as any)?.errorCode;
            
            if (errorCode === ERROR_CODE_NO_BILL_ITEMS) {
                setShowSnackbar({
                    show: true,
                    message: 'hospital.billing.insurance_bills.error.no_bill_items',
                    severity: 'error'
                });
            } else {
                setShowSnackbar({
                    show: true,
                    message: err instanceof Error ? err.message : 'hospital.billing.insurance_bills.error.failed_to_close_bill',
                    severity: 'error'
                });
            }
        } finally {
            setClosing(false);
        }
    };

    const handleCloseModal = useCallback(() => {
        setShowCloseConfirm(false);
        setShowModalPDF(false);
        setShowModalPaymentRecords(false);
    }, []);

    // Handle when billItems are marked as paid
    const handleUpdateClaimStatus = useCallback(async (items: BillItem[], paymentRecord?: CreatePaymentRequest) => {
        console.log('[InsuranceBillView] handleUpdateClaimStatus called', { 
            itemsCount: items.length, 
            paymentRecord,
            billUuid: bill?.uuid
        });
        
        if (!bill) {
            console.log('[InsuranceBillView] No bill found, returning early');
            return;
        }

        try {
            // Find which items were marked as paid and check if they're not already PAID
            const paidItems = items.filter(item => {
                const isPaid = item.paid === true as boolean;
                console.log('[InsuranceBillView] Checking item:', { 
                    concept: item.concept, 
                    paid: item.paid, 
                    isPaid 
                });
                return isPaid;
            });

            console.log('[InsuranceBillView] Paid items found:', paidItems.length);

            if (paidItems.length === 0) {
                console.log('[InsuranceBillView] No paid items found, returning early');
                return;
            }

            // Clean billItems before sending to API (remove client-side only properties)
            const cleanBillItems = items.map(item => {
                const { patientName, patientPaymentAmount, patientBillCreatedAt, ...cleanItem } = item as BillItem & {
                    patientName?: string;
                    patientPaymentAmount?: number;
                    patientBillCreatedAt?: Date;
                };
                return cleanItem;
            });

            const minimalBill = {
                ...bill,
                billItems: cleanBillItems
            } as InsuranceBill;

            console.log('[InsuranceBillView] About to call API', {
                hasPaymentRecord: !!paymentRecord,
                paymentRecord,
                billUuid: bill.uuid,
                investigationUuid,
                cleanBillItemsCount: cleanBillItems.length
            });

            if (paymentRecord) {
                console.log('[InsuranceBillView] Calling updateBillItemsStatusService...');
                await updateBillItemsStatusService(investigationUuid, bill.uuid, minimalBill as unknown as Bill, paymentRecord);
                console.log('[InsuranceBillView] API call completed successfully');
            } else {
                console.log('[InsuranceBillView] WARNING: No paymentRecord provided, skipping API call');
            }

            setShowSnackbar({
                show: true,
                message: 'hospital.billing.insurance_bills.success.claims_updated',
                severity: 'success'
            });
            
            refetch();
            refetchPaymentRecords();
            onBillUpdated?.();
        } catch (err: unknown) {
            // Check for specific error codes from the API
            const errorCode = (err as any)?.response?.data?.errorCode || (err as any)?.errorCode;
            let errorMessage = 'hospital.billing.insurance_bills.error.failed_to_update_claims';
            
            // Map error codes to translation keys
            if (errorCode) {
                const errorCodeMap: Record<string, string> = {
                    'PAYMENT_EXCEEDS_REMAINING_BALANCE': 'hospital.billing.insurance_bills.error.payment_exceeds_remaining_balance',
                    'NO_BILL_ITEMS': 'hospital.billing.insurance_bills.error.no_bill_items',
                    'INVALID_PAYMENT_AMOUNT': 'hospital.billing.insurance_bills.error.invalid_payment_amount',
                    'BILL_NOT_FOUND': 'hospital.billing.insurance_bills.error.bill_not_found',
                    'BILL_ALREADY_CLOSED': 'hospital.billing.insurance_bills.error.bill_already_closed',
                };
                
                errorMessage = errorCodeMap[errorCode] || errorMessage;
            }
            
            setShowSnackbar({
                show: true,
                message: errorMessage,
                severity: 'error'
            });
        }
    }, [bill, claims, investigationUuid, refetch, refetchPaymentRecords, onBillUpdated, setShowSnackbar]);

    const handleDownloadBillPDF = useCallback(async () => {
        if (!bill || !pdfConfig) {
            return;
        }

        try {
            setGeneratingPDF(true);
            await generateInsuranceBillPDF(
                bill,
                billItemsWithPatientData,
                paymentRecords,
                pdfConfig,
                currency,
                languageCode
            );
            setShowModalPDF(false);
        } catch (error) {
            console.error('Error generating PDF:', error);
            setShowSnackbar({
                show: true,
                message: 'Failed to generate PDF',
                severity: 'error'
            });
        } finally {
            setGeneratingPDF(false);
        }
    }, [bill, pdfConfig, billItemsWithPatientData, currency, languageCode, paymentRecords, setShowSnackbar]);

    const renderModal = useCallback(() => {
        if (showCloseConfirm) {
            return (
                <Dialog
                    open={true}
                    onClose={handleCloseModal}
                    maxWidth="sm"
                    fullWidth
                    PaperProps={{
                        sx: {
                            borderRadius: 2,
                            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)'
                        }
                    }}
                >
                    <DialogTitle sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'space-between',
                        pb: 2,
                        pt: 3,
                        px: 3
                    }}>
                        <Typography variant="h6" component="span" sx={{ fontWeight: 600 }}>
                            <Translate id="hospital.billing.insurance_bills.close_bill_confirm_title" />
                        </Typography>
                        <IconButton
                            onClick={handleCloseModal}
                            size="small"
                            sx={{
                                color: 'text.secondary',
                                '&:hover': {
                                    backgroundColor: 'action.hover'
                                }
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent sx={{ px: 3, pb: 2 }}>
                        <Box sx={{ 
                            display: 'flex', 
                            flexDirection: 'column', 
                            alignItems: 'center', 
                            gap: 2.5,
                            textAlign: 'center',
                            py: 1
                        }}>
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 72,
                                height: 72,
                                borderRadius: '50%',
                                backgroundColor: 'warning.light',
                                color: 'warning.main',
                                flexShrink: 0
                            }}>
                                <WarningAmberIcon sx={{ fontSize: 48 }} />
                            </Box>
                            <Typography 
                                variant="body1" 
                                component="div"
                                sx={{ 
                                    color: 'text.primary',
                                    fontWeight: 400,
                                    lineHeight: 1.7,
                                    maxWidth: '500px',
                                    fontSize: '0.95rem'
                                }}
                            >
                                <Translate>
                                    {({ translate }) => (
                                        <span dangerouslySetInnerHTML={{ 
                                            __html: translate('hospital.billing.insurance_bills.close_bill_confirm_message') as string 
                                        }} />
                                    )}
                                </Translate>
                            </Typography>
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{ 
                        px: 3, 
                        pb: 3, 
                        pt: 1,
                        gap: 1.5,
                        justifyContent: 'flex-end'
                    }}>
                        <Button 
                            onClick={handleCloseModal} 
                            variant="outlined"
                            color="inherit"
                            sx={{
                                minWidth: 100,
                                textTransform: 'none',
                                fontWeight: 500,
                                borderColor: 'divider',
                                color: 'text.secondary',
                                '&:hover': {
                                    borderColor: 'text.secondary',
                                    backgroundColor: 'action.hover'
                                }
                            }}
                            disabled={closing}
                        >
                            <Translate id="general.cancel" />
                        </Button>
                        <Button 
                            onClick={handleCloseBill}
                            variant="contained"
                            color="primary"
                            sx={{
                                minWidth: 120,
                                textTransform: 'none',
                                fontWeight: 600,
                                backgroundColor: 'primary.main',
                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                                '&:hover': {
                                    backgroundColor: 'primary.dark',
                                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)'
                                },
                                '&:disabled': {
                                    backgroundColor: 'action.disabledBackground'
                                }
                            }}
                            disabled={closing}
                        >
                            <Translate id="general.confirm" />
                        </Button>
                    </DialogActions>
                </Dialog>
            );
        } else if (showModalPDF) {
            return (
                <Modal
                    key="modal-pdf"
                    maxWidth="xs"
                    open={true}
                    title=""
                    closeModal={handleCloseModal}
                >
                    {!pdfConfig ? (
                        <Loader />
                    ) : (
                        <Grid container spacing={2} justifyContent="center" style={{ padding: '16px' }}>
                            <Grid item xs={12} style={{ textAlign: 'center' }}>
                                <Button
                                    variant="contained"
                                    startIcon={<PictureAsPdfIcon />}
                                    onClick={handleDownloadBillPDF}
                                    disabled={generatingPDF}
                                    fullWidth
                                >
                                    <Translate id="hospital.billing.bill.download" />
                                </Button>
                            </Grid>
                        </Grid>
                    )}
                </Modal>
            );
        } else if (showModalPaymentRecords) {
            return (
                <PaymentRecordsModal
                    open={true}
                    paymentRecords={paymentRecords}
                    paymentMethods={paymentMethods}
                    researcherMap={researcherMap}
                    currency={currency}
                    languageCode={languageCode}
                    onClose={() => setShowModalPaymentRecords(false)}
                />
            );
        }
        return null;
    }, [showCloseConfirm, showModalPDF, showModalPaymentRecords, closing, handleCloseModal, handleCloseBill, pdfConfig, generatingPDF, handleDownloadBillPDF, paymentRecords, paymentMethods, researcherMap, currency, languageCode]);

    if (loading) {
        return <Loader />;
    }

    if (error || !bill) {
        return (
            <Card style={{ padding: '2rem', textAlign: 'center', margin: '10px' }}>
                <Typography color="error">{error || 'Bill not found'}</Typography>
                {onClose && (
                    <Button onClick={onClose} style={{ marginTop: '1rem' }}>
                        <Translate id="general.back" />
                    </Button>
                )}
            </Card>
        );
    }

    return (
        <>
            <Snackbar
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                open={showSnackbar.show}
                autoHideDuration={SNACKBAR_AUTO_HIDE_DURATION}
                onClose={handleCloseSnackbar}
            >
                <Alert 
                    onClose={handleCloseSnackbar} 
                    severity={showSnackbar.severity as 'success' | 'error' | 'warning' | 'info'} 
                    sx={{ width: '100%' }}
                >
                    <Translate id={showSnackbar.message} />
                </Alert>
            </Snackbar>
            {renderModal()}

            <Card style={{ margin: '10px' }}>
                <Box padding={2}>
                    <Grid container paddingBottom={'1rem'}>
                        <Grid item xs={6}>
                            <Typography variant="body2">
                                <span style={{ fontWeight: 'bold' }}>
                                    <Translate id="hospital.billing.insurance_bills.organization" />
                                </span>: {bill.organizationName} {bill.coveragePercentage ? `(${bill.coveragePercentage}%)` : ''}
                            </Typography>
                            <Typography variant="body2">
                                <span style={{ fontWeight: 'bold' }}>
                                    <Translate id="hospital.billing.insurance_bills.title" />
                                </span>
                            </Typography>
                            <Typography variant="body2">
                                <span style={{ fontWeight: 'bold' }}>
                                    <Translate id="hospital.billing.insurance_bills.bill_type" />:
                                </span> Assurance
                            </Typography>
                            {getBillIdentifier(bill) && (
                                <Typography variant="body2">
                                    <span style={{ fontWeight: 'bold' }}>
                                        <Translate id="hospital.billing.bill.bill_id" />:
                                    </span> {getBillIdentifier(bill)}
                                </Typography>
                            )}
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="body2">
                                <span style={{ fontWeight: 'bold' }}>
                                    <Translate id="hospital.billing.insurance_bills.bill_date" />
                                </span>: {fullDateFromPostgresString(languageCode, bill.createdAt)}
                            </Typography>
                            <Typography variant="body2">
                                <span style={{ fontWeight: 'bold' }}>
                                    <Translate id="hospital.billing.bill.status.label" />
                                </span>: <ColourChip
                                rgbcolor={documentStatusToColor(bill.status)}
                                label={<Translate id={`hospital.billing.bill.status.${bill.status === DocumentStatus.DRAFT ? 'draft' : 'closed'}`} />}
                                avatar={documentStatusToIcon(bill.status)}
                            />
                            </Typography>
                            {bill.status === DocumentStatus.CLOSED && (
                                <Box display="flex" alignItems="center" mb={1}>
                                    <Typography component="span" variant="body2" fontWeight="bold" mr={1}>
                                        <Translate id="hospital.billing.bill.view_pdf" />
                                    </Typography>
                                    <Button onClick={() => setShowModalPDF(true)} size="small" style={{ minWidth: 0, padding: 6 }}>
                                        <IconGenerator type="pdf" />
                                    </Button>
                                </Box>
                            )}
                            {bill.status === DocumentStatus.DRAFT && (
                                <Box display="flex" alignItems="center" mb={1} mt={1}>
                                    <Button 
                                        onClick={() => setShowCloseConfirm(true)} 
                                        variant="contained"
                                        color="primary"
                                        size="small"
                                        sx={{
                                            textTransform: 'none',
                                            fontWeight: 500
                                        }}
                                    >
                                        <Translate id="hospital.billing.insurance_bills.close_bill" />
                                    </Button>
                                </Box>
                            )}
                        </Grid>
                    </Grid>
                </Box>
            </Card>

            <Card style={{ margin: '10px' }}>
                <Grid container justifyContent="center" padding={2}>
                    <Grid item xs={6}>
                        <Box display="flex" alignItems="center" mb={1}>
                            <Typography component="span" variant="body2" fontWeight="bold" mr={1} mb={0}>
                                <Translate id="hospital.billing.bill.insurance.total_cost" /> :
                            </Typography>
                            <Chip
                                label={formatCurrency(bill.totalGross, currency, languageCode)}
                                color="primary"
                                style={{ fontWeight: 'bold', backgroundColor: '#1976d2', color: '#fff', marginRight: 8 }}
                                size="small"
                            />
                        </Box>
                        <Box display="flex" alignItems="center" mb={1}>
                            <Typography component="span" variant="body2" fontWeight="bold" mr={1} mb={0}>
                                <Translate id="hospital.billing.insurance_bills.claims" /> :
                            </Typography>
                            <Chip
                                label={claimsCount.toString()}
                                color="primary"
                                style={{ fontWeight: 'bold', backgroundColor: '#3579f8', color: '#fff', marginRight: 8 }}
                                size="small"
                            />
                        </Box>
                    </Grid>
                    <Grid item xs={6}>
                        <PaymentRecordsDisplay
                            paymentRecords={paymentRecords}
                            currency={currency}
                            languageCode={languageCode}
                            translationKey="pages.hospital.billing.pdf.insurance.total_paid"
                            onViewPaymentRecords={() => setShowModalPaymentRecords(true)}
                            showViewButton={bill.status !== DocumentStatus.DRAFT}
                        />
                        <Box display="flex" alignItems="center">
                            <Typography component="span" variant="body2" fontWeight="bold" mr={1} mb={0}>
                                <Translate id="hospital.billing.bill.remaining" /> :
                            </Typography>
                            <Chip
                                label={formatCurrency(totalPending, currency, languageCode)}
                                color="primary"
                                style={{ fontWeight: 'bold', backgroundColor: totalPending > 0 ? '#f44336' : '#4CAF50', color: '#fff', marginRight: 8 }}
                                size="small"
                            />
                        </Box>
                    </Grid>
                </Grid>
            </Card>
            {billItemsWithPatientData.length > 0 && (
                <Card style={{ margin: '10px' }}>
                    <Box padding={2}>
                        <BillItems
                            columns={INSURANCE_BILL_COLUMNS}
                            uuidPatient={undefined}
                            mode={BillItemModes.SHOW}
                            initItems={billItemsWithPatientData}
                            repeatBillItems={true}
                            showTotal={true}
                            canUseAdditionalInfo={false}
                            surveyAdditionalInfo={undefined}
                            currency={currency}
                            print={false}
                            withDiscount={false}
                            bill={{
                                ...bill,
                                type: DocumentType.INVOICE,
                                status: bill.status,
                                uuidPatient: undefined,
                            } as unknown as Bill}
                            canUpdateBill={false}
                            uuidInvestigation={investigationUuid}
                            onBillItemsValidated={() => {}}
                            onUpdateBillItemStatus={handleUpdateClaimStatus}
                            error={undefined}
                            isPaid={bill.status === DocumentStatus.DRAFT || remainingBalance === 0}
                            onCancelBill={() => {}}
                            onPaymentSuccess={() => {
                                refetch();
                                refetchPaymentRecords();
                                onBillUpdated?.();
                            }}
                        />
                    </Box>
                </Card>
            )}
        </>
    );
};

export default InsuranceBillView;

